import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, CheckCircle2, Copy, ExternalLink, FolderUp, Inbox, Lock, Plus, ShieldCheck, UploadCloud, X } from "lucide-react";
import { generateToken } from "@limeshare/lsc1";
import { api } from "../api";
import { FileIcon } from "../components/FileIcon";
import { SpeedTracker, type TransferStats } from "../engine/speed";
import { uploadTransfer, type UploadProgress } from "../engine/upload";
import { errorMessage, formatBytes } from "../util";

interface SavedPortal {
  slug: string;
  title: string;
  ownerToken: string;
  createdAt: number;
}

const PORTAL_STORAGE_KEY = "limeshare.portals.v1";

function loadSavedPortals(): SavedPortal[] {
  if (typeof localStorage === "undefined") return [];
  try {
    return JSON.parse(localStorage.getItem(PORTAL_STORAGE_KEY) ?? "[]");
  } catch {
    return [];
  }
}

function savePortal(p: SavedPortal): void {
  if (typeof localStorage === "undefined") return;
  try {
    const existing = loadSavedPortals().filter((x) => x.slug !== p.slug);
    localStorage.setItem(PORTAL_STORAGE_KEY, JSON.stringify([p, ...existing].slice(0, 30)));
  } catch {
    /* ignore */
  }
}

export function RequestFilesPage() {
  const { slug } = useParams();
  return slug ? <PortalClientView slug={slug} /> : <PortalCreatorHub />;
}

function PortalCreatorHub() {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [portals, setPortals] = useState<SavedPortal[]>([]);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);
  const [copiedEmbed, setCopiedEmbed] = useState<string | null>(null);

  useEffect(() => {
    setPortals(loadSavedPortals());
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (title.trim().length < 2) return setError("Enter a portal name (at least 2 characters).");
    setError(null);
    setBusy(true);
    try {
      const ownerToken = generateToken();
      const res = await api.createRequestPortal({
        title: title.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        ownerToken,
      });
      const created: SavedPortal = { slug: res.slug, title: res.title, ownerToken, createdAt: Date.now() };
      savePortal(created);
      setPortals(loadSavedPortals());
      setTitle("");
      setDescription("");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = async (slug: string) => {
    await navigator.clipboard.writeText(`${window.location.origin}/r/${slug}`);
    setCopiedSlug(slug);
    setTimeout(() => setCopiedSlug(null), 2000);
  };

  const copyEmbedCode = async (slug: string, portalTitle: string) => {
    const origin = window.location.origin;
    const html = `<!-- LimeShare Zero-Knowledge Upload Widget -->\n<iframe src="${origin}/embed/r/${slug}" width="100%" height="460" style="border:1px solid #e3ebe4;border-radius:16px;" loading="lazy"></iframe>\n<p style="font-size:12px;text-align:right;margin-top:4px;"><a href="${origin}/request-files" target="_blank" rel="noopener">Encrypted File Upload by LimeShare</a></p>`;
    await navigator.clipboard.writeText(html);
    setCopiedEmbed(slug);
    setTimeout(() => setCopiedEmbed(null), 2500);
  };

  return (
    <article className="panel legal" style={{ maxWidth: 880, margin: "0 auto" }}>
      <header style={{ marginBottom: 28 }}>
        <span className="badge" style={{ marginBottom: 12 }}>
          <Inbox size={13} /> Reverse Drop · Zero-Knowledge File Requests
        </span>
        <h1 style={{ fontSize: "clamp(28px, 4.2vw, 42px)", fontWeight: 900, lineHeight: 1.12, margin: "0 0 12px" }}>
          Request &amp; Receive Large Files from Clients Without Login
        </h1>
        <p className="hint" style={{ fontSize: 16, margin: 0 }}>
          Create a dedicated upload link in 5 seconds. Send it to any client, patient, or contractor — they drop up to 5 GB of files or folders, encrypted in their browser directly for you.
        </p>
      </header>

      {/* Interactive Portal Generator */}
      <form onSubmit={handleCreate} style={{ padding: 24, background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", marginBottom: 32 }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 18 }}>Create Your Free Client Upload Portal</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <input
            className="textinput"
            style={{ marginTop: 0 }}
            placeholder="Portal Title (e.g. 'Alex Studio — Upload Raw Footage' or 'Smith Law — Discovery Drop')"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
          <textarea
            className="textarea"
            rows={2}
            placeholder="Optional instructions for your clients (e.g. 'Please drop your 4K MOV clips or tax PDFs here')"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          {error && <div className="alert error" style={{ marginTop: 0 }}>{error}</div>}
          <button className="btn" type="submit" disabled={busy} style={{ alignSelf: "flex-start" }}>
            <Plus size={16} /> {busy ? "Creating Portal…" : "Create Receive-Files Link (Free)"}
          </button>
        </div>
      </form>

      {/* Active Portals Created in This Browser */}
      {portals.length > 0 && (
        <section style={{ marginBottom: 36 }}>
          <h2 style={{ fontSize: 20, marginBottom: 12 }}>Your Active Receive Portals</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {portals.map((p) => {
              const publicUrl = `${typeof window !== "undefined" ? window.location.origin : "https://limeshare.org"}/r/${p.slug}`;
              return (
                <div key={p.slug} className="file" style={{ flexWrap: "wrap", padding: "14px 16px" }}>
                  <div>
                    <b style={{ display: "block", fontSize: 15 }}>{p.title}</b>
                    <code style={{ fontSize: 12, color: "var(--lime)" }}>{publicUrl}</code>
                  </div>
                  <div className="file-actions">
                    <button className="btn ghost btn-sm" onClick={() => copyUrl(p.slug)}>
                      {copiedSlug === p.slug ? <Check size={13} /> : <Copy size={13} />}
                      <span>{copiedSlug === p.slug ? "Copied" : "Copy Link"}</span>
                    </button>
                    <button className="btn ghost btn-sm" onClick={() => copyEmbedCode(p.slug, p.title)} title="Embed this upload box on your own website">
                      {copiedEmbed === p.slug ? <Check size={13} color="var(--lime)" /> : <Copy size={13} />}
                      <span>{copiedEmbed === p.slug ? "Embed Copied!" : "Embed HTML <iframe>"}</span>
                    </button>
                    <Link to={`/r/${p.slug}?owner=${encodeURIComponent(p.ownerToken)}`} className="btn btn-sm">
                      <Inbox size={13} /> Open Inbox
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Comprehensive SEO Longread for File Requests */}
      <section style={{ marginBottom: 32 }}>
        <h2>Why Professionals Use LimeShare File Requests Instead of Dropbox or Google Drive</h2>
        <p>
          Collecting large files from outside clients has always been a friction-filled nightmare. If you ask a client to upload a
          3 GB video or confidential tax PDF to <b>Google Drive</b> or <b>OneDrive</b>, they are forced to log into a Google/Microsoft
          account, request folder edit permissions, and consume their own personal cloud storage quota.
        </p>
        <p>
          <b>Dropbox File Requests</b> and <b>WeTransfer Portals</b> lock large file collection behind $20–$30/month paywalls and store
          client uploads unencrypted on corporate servers. LimeShare Reverse Drop solves both problems:
        </p>

        <table>
          <thead>
            <tr>
              <th>Feature</th>
              <th style={{ color: "var(--lime)" }}>LimeShare Reverse Drop</th>
              <th>Dropbox File Requests</th>
              <th>Google Drive Shared Folder</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><b>Cost to Receive 5 GB Files</b></td>
              <td style={{ color: "var(--lime)", fontWeight: 700 }}>100% Free</td>
              <td>Paid subscription required for &gt;2 GB</td>
              <td>Consumes your &amp; client's storage quota</td>
            </tr>
            <tr>
              <td><b>Client Account Required?</b></td>
              <td style={{ color: "var(--lime)", fontWeight: 700 }}>Never (Zero login friction)</td>
              <td>Optional</td>
              <td>Mandatory Google Sign-In</td>
            </tr>
            <tr>
              <td><b>Encryption</b></td>
              <td style={{ color: "var(--lime)", fontWeight: 700 }}>Client-Side Zero-Knowledge (AES-256-GCM)</td>
              <td>Server-side plaintext access</td>
              <td>Scanned &amp; indexed by Google</td>
            </tr>
            <tr>
              <td><b>Folder Uploads Supported?</b></td>
              <td style={{ color: "var(--lime)", fontWeight: 700 }}>Yes (Up to 500 files per drop)</td>
              <td>Yes</td>
              <td>Often fails on mobile browsers</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Top Use Cases for Receive-Files Links</h2>
        <ul>
          <li><b>Law Firms &amp; Paralegals:</b> Collect client evidence, PDF discovery bundles, and dashcam video under strict zero-knowledge confidentiality.</li>
          <li><b>Accountants &amp; Tax Advisors:</b> Receive W-2s, bank statements, and QuickBooks backups without emailing unencrypted attachments.</li>
          <li><b>Video Editors &amp; Agencies:</b> Give clients and wedding videographers a single link to drop raw 4K MOV/MP4 camera cards.</li>
          <li><b>Print Shops &amp; Architects:</b> Receive 500 MB Photoshop PSD, Illustrator AI, and AutoCAD DWG files from customers with zero email bounces.</li>
        </ul>
      </section>
    </article>
  );
}

function PortalClientView({ slug }: { slug: string }) {
  const [portal, setPortal] = useState<{
    slug: string;
    title: string;
    description: string | null;
    isOwner: boolean;
    submissions: { id: number; transferId: string; shareLink: string; senderName: string | null; note: string | null; createdAt: number }[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [files, setFiles] = useState<File[]>([]);
  const [senderName, setSenderName] = useState("");
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [stats, setStats] = useState<TransferStats | null>(null);
  const [doneLink, setDoneLink] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const folderRef = useRef<HTMLInputElement>(null);
  const speedTracker = useRef(new SpeedTracker());

  const loadPortal = async () => {
    setLoading(true);
    try {
      const urlOwner = new URLSearchParams(window.location.search).get("owner");
      const saved = loadSavedPortals().find((x) => x.slug === slug);
      const token = urlOwner || saved?.ownerToken;
      const data = await api.getRequestPortal(slug, token);
      setPortal(data);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadPortal();
  }, [slug]);

  const handleUploadToPortal = async () => {
    if (files.length === 0) return;
    setError(null);
    setUploading(true);
    speedTracker.current.start();
    try {
      const res = await uploadTransfer({
        files,
        burn: false,
        ...(note.trim() ? { message: note.trim() } : {}),
        onProgress: (p) => {
          setProgress(p);
          setStats(speedTracker.current.update(p.bytesDone, p.bytesTotal));
        },
      });
      await api.submitToPortal(slug, {
        transferId: res.transferId,
        shareLink: res.link,
        ...(senderName.trim() ? { senderName: senderName.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setDoneLink(res.link);
      setFiles([]);
      void loadPortal();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="center">Loading encrypted upload portal…</div>;
  if (error && !portal) {
    return (
      <section className="panel">
        <h2>Portal not found</h2>
        <p className="hint">{error}</p>
        <Link to="/request-files" className="btn btn-sm">Create your own portal</Link>
      </section>
    );
  }
  if (!portal) return null;

  return (
    <section className="panel glow" style={{ maxWidth: 680, margin: "0 auto" }}>
      <span className="badge" style={{ marginBottom: 10 }}>
        <Lock size={12} /> Zero-Knowledge Client Upload Portal
      </span>
      <h1 style={{ margin: "0 0 6px", fontSize: 26, fontWeight: 900 }}>{portal.title}</h1>
      <p className="hint" style={{ margin: "0 0 20px" }}>
        {portal.description || "Drop files or folders below. Everything is encrypted in your browser before upload."}
      </p>

      {doneLink ? (
        <div className="alert ok">
          <CheckCircle2 size={18} style={{ verticalAlign: "middle", marginRight: 6 }} />
          <b>Files encrypted and delivered to {portal.title}!</b>
          <div style={{ marginTop: 10 }}>
            <button className="btn btn-sm" onClick={() => setDoneLink(null)}>Send more files</button>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div className="widget-pick-btns">
            <button type="button" className="widget-pick-btn file" disabled={uploading} onClick={() => inputRef.current?.click()}>
              <UploadCloud size={16} /> Select Files
            </button>
            <button type="button" className="widget-pick-btn folder" disabled={uploading} onClick={() => folderRef.current?.click()}>
              <FolderUp size={16} /> Select Folder
            </button>
          </div>

          <input
            ref={inputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files) setFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
              e.target.value = "";
            }}
          />
          <input
            ref={folderRef}
            type="file"
            // @ts-expect-error webkitdirectory
            webkitdirectory=""
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files) setFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
              e.target.value = "";
            }}
          />

          {files.length > 0 && (
            <ul className="files">
              {files.map((f, i) => (
                <li className="file" key={i}>
                  <div className="file-info">
                    <FileIcon name={f.name} type={f.type} size={18} />
                    <span className="name">{f.name}</span>
                    <span className="size">{formatBytes(f.size)}</span>
                  </div>
                  {!uploading && (
                    <button className="btn ghost btn-sm btn-icon" onClick={() => setFiles(files.filter((_, j) => j !== i))}>
                      <X size={13} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          <input
            className="textinput"
            placeholder="Your name or company (optional)"
            value={senderName}
            disabled={uploading}
            onChange={(e) => setSenderName(e.target.value)}
          />
          <textarea
            className="textarea"
            rows={2}
            placeholder="Message or project notes (optional)"
            value={note}
            disabled={uploading}
            onChange={(e) => setNote(e.target.value)}
          />

          {error && <div className="alert error">{error}</div>}

          {uploading ? (
            <div className="progress">
              <div className="bar"><div style={{ width: `${stats?.percent ?? 0}%` }} /></div>
              <div className="meta">
                <span>Encrypting &amp; delivering {progress?.fileName}…</span>
                <span>{stats?.speedFormatted} · {stats?.percent ?? 0}%</span>
              </div>
            </div>
          ) : (
            <button className="btn block" disabled={files.length === 0} onClick={handleUploadToPortal}>
              <Lock size={16} /> Encrypt &amp; Deliver to {portal.title}
            </button>
          )}
        </div>
      )}

      {/* Owner Inbox View */}
      {portal.isOwner && (
        <div style={{ marginTop: 28, paddingTop: 20, borderTop: "1px solid var(--border)" }}>
          <h3 style={{ margin: "0 0 10px", fontSize: 17 }}>
            <Inbox size={16} style={{ verticalAlign: "middle", marginRight: 6 }} />
            Owner Inbox ({portal.submissions.length} deliveries received)
          </h3>
          {portal.submissions.length === 0 ? (
            <p className="hint">No files received yet. Share <code>{window.location.origin}/r/{portal.slug}</code> with your clients.</p>
          ) : (
            <ul className="files" style={{ maxHeight: 340 }}>
              {portal.submissions.map((s) => (
                <li key={s.id} className="file" style={{ flexWrap: "wrap" }}>
                  <div>
                    <b>{s.senderName || "Anonymous Client"}</b>
                    <small className="hint" style={{ marginLeft: 8 }}>{new Date(s.createdAt).toLocaleString()}</small>
                    {s.note && <div className="hint" style={{ fontSize: 12 }}>{s.note}</div>}
                  </div>
                  <a href={s.shareLink} target="_blank" rel="noopener noreferrer" className="btn btn-sm">
                    <ExternalLink size={13} /> Open &amp; Decrypt Files
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
